import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Video, 
  PhoneCall, 
  Users, 
  CheckCircle2, 
  Lock, 
  Send, 
  Calendar, 
  Clock, 
  ArrowRight, 
  Sparkles, 
  ShieldCheck,
  Smile,
  Heart,
  HelpCircle,
  Laptop,
  Check,
  Eye,
  TrendingUp,
  FileUp,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { SEO } from '../components/SEO';

export default function MarteMeo() {
  const [activeAccordion, setActiveAccordion] = useState(null);

  const toggleAccordion = (index) => {
    setActiveAccordion(activeAccordion === index ? null : index);
  };
  return (
    <div className="bg-white overflow-hidden">
      <SEO 
        title="Marte Meo | Styrk samspillet med konkrete redskaber"
        description="Få konkrete redskaber til hverdagen med Marte Meo. Gennem korte videooptagelser finder vi det, der virker, og mindsker konflikter. Læs mere og book her."
        path="/marte-meo"
      />
      {/* Hero Section */}
      <section className="relative h-[55vh] md:h-[65vh] min-h-[400px] md:min-h-[500px] flex items-center justify-center overflow-hidden">
        <img 
          src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/eff3456d-e9a8-4cc4-936d-27bf51e95b8f/5b19b1f6-e31a-4205-83d0-bb137335da93.png" 
          alt="Marte Meo samspil og øjenkontakt" 
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-stone-900/50 via-stone-900/40 to-stone-900/60" />
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-24 md:pt-32">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-6xl font-light text-white uppercase tracking-tight mt-2"
          >
            Marte Meo
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-stone-200 text-lg md:text-xl max-w-2xl mx-auto mt-4 font-light leading-relaxed"
          >
            Find jeres egne styrker og skab varig trivsel gennem korte hverdagsfilm.
          </motion.p>
        </div>

        {/* Wave Divider at bottom of Hero */}
        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none z-20">
          <svg 
            viewBox="0 0 1200 120" 
            preserveAspectRatio="none" 
            className="relative block w-full h-12 md:h-24 fill-stone-50"
          >
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V120H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"></path>
          </svg>
        </div>
      </section>

      {/* Unified Section: Hvad får du ud af Marte Meo & Sådan udvikler I jer */}
      <section className="pt-10 md:pt-14 pb-8 md:pb-10 bg-stone-50 relative overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Main Header */}
          <div className="max-w-3xl mb-12 text-left">
            <h2 className="text-3xl md:text-4xl font-light text-stone-900 uppercase tracking-tight mt-2">
              Hvad får du ud af et Marte Meo-forløb?
            </h2>
            <div className="w-12 h-1 bg-[#4e6354] my-6"></div>
            <p className="text-lg text-stone-600 font-light leading-relaxed">
              Marte Meo betyder <strong>”af egen kraft”</strong>. Vi filmer korte videoer af din hverdag med dit barn. Ud fra videoerne finder vi alt det, du allerede gør godt, og bygger videre på det.
            </p>
          </div>

          {/* Main Grid: 3 columns on desktop */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-10 items-stretch">
            
            {/* Column 1: Det får I ud af det */}
            <div className="lg:col-span-4 flex">
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="w-full bg-white/80 backdrop-blur-md p-6 md:p-8 rounded-3xl border border-stone-200/60 border-l-4 border-l-[#4e6354] shadow-sm flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-[#4e6354]/10 flex items-center justify-center text-[#4e6354]">
                      <Smile className="w-5 h-5" />
                    </div>
                    <h3 className="text-lg font-semibold text-stone-900">Det får I ud af det</h3>
                  </div>
                  <ul className="space-y-4">
                    {[
                      { title: "Tydelige redskaber", desc: "Du ser præcis, hvad der virker derhjemme." },
                      { title: "Mindre frustration", desc: "Du lærer at forstå dit barns signaler." },
                      { title: "Mere ro", desc: "I får færre konflikter i hverdagen." },
                      { title: "Stærkere bånd", desc: "Du får mere glæde og bedre kontakt med dit barn." },
                      { title: "Større selvtillid", desc: "Du bliver mere tryg i rollen som forælder." }
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <div className="mt-1 w-5 h-5 rounded-full bg-[#4e6354]/10 flex items-center justify-center text-[#4e6354] shrink-0">
                          <Check className="w-3 h-3 stroke-[3]" />
                        </div>
                        <div>
                          <strong className="text-stone-800 block font-medium text-sm md:text-base">{item.title}</strong>
                          <span className="text-stone-600 text-sm font-light">{item.desc}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            </div>

            {/* Column 2: Det kræver det af jer */}
            <div className="lg:col-span-4 flex">
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="w-full bg-white/80 backdrop-blur-md p-6 md:p-8 rounded-3xl border border-stone-200/60 border-l-4 border-l-[#4e6354] shadow-sm flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-[#4e6354]/10 flex items-center justify-center text-[#4e6354]">
                      <Users className="w-5 h-5" />
                    </div>
                    <h3 className="text-lg font-semibold text-stone-900">Det kræver det af jer</h3>
                  </div>
                  <ul className="space-y-4">
                    {[
                      { title: "Korte videoer", desc: "Vi filmer 7-10 minutter af en hverdagssituation." },
                      { title: "Samtale", desc: "Vi ser videoen sammen og taler om den." },
                      { title: "Åbenhed", desc: "Du har lyst til at kigge på dit eget samspil med barnet." },
                      { title: "Små skridt", desc: "Du prøver nye, små ting af derhjemme." }
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <div className="mt-1 w-5 h-5 rounded-full bg-[#4e6354]/10 flex items-center justify-center text-[#4e6354] shrink-0">
                          <Check className="w-3 h-3 stroke-[3]" />
                        </div>
                        <div>
                          <strong className="text-stone-800 block font-medium text-sm md:text-base">{item.title}</strong>
                          <span className="text-stone-600 text-sm font-light">{item.desc}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            </div>

            {/* Column 3: Beautiful Image with Green Glow */}
            <div className="lg:col-span-4 relative flex items-center justify-center mt-8 lg:mt-0">
              {/* Green Glow behind image */}
              <div className="absolute -inset-4 bg-[#4e6354]/20 rounded-[120px] blur-2xl z-0 pointer-events-none"></div>
              
              <div className="relative w-full">
                <div className="w-full aspect-[4/5] rounded-3xl rounded-tr-[100px] rounded-bl-[100px] overflow-hidden shadow-lg border border-stone-200/40 relative z-10">
                  <img 
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/eff3456d-e9a8-4cc4-936d-27bf51e95b8f/img-98211-2_1.jpg" 
                    alt="Marte Meo samspil og glæde" 
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700 ease-out"
                  />
                </div>

                {/* Floating Badge */}
                <motion.div 
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3, duration: 0.5 }}
                  className="absolute -bottom-6 -right-4 sm:-right-8 bg-white/95 backdrop-blur-md rounded-2xl p-5 border border-stone-200/50 shadow-xl max-w-[240px] z-20"
                >
                  <div className="flex items-center gap-2 justify-center text-[#4e6354] mb-1">
                    <Heart className="w-4 h-4 fill-current" />
                    <span className="text-xs font-semibold uppercase tracking-wider text-stone-800">Marte Meo</span>
                  </div>
                  <span className="block text-sm text-stone-600 font-medium text-center leading-relaxed">
                    Mødeøjeblik
                  </span>
                </motion.div>
              </div>
            </div>

          </div>

          {/* Subsection: Sådan udvikler I jer */}
          <div className="mt-12 pt-8 border-t border-stone-200/60 max-w-6xl mx-auto">
            <div className="mb-12 text-center max-w-3xl mx-auto">
              <h3 className="text-2xl md:text-3xl font-light text-stone-900 uppercase tracking-tight mb-4">
                Sådan udvikler I jer
              </h3>
              <p className="text-stone-600 font-light leading-relaxed">
                Gennem forløbet vil du opleve en mærkbar udvikling i din forældrerolle og i jeres fælles hverdag.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { title: "Fra tvivl til sikkerhed", desc: "Du ved præcis, hvad dit barn har brug for.", icon: ShieldCheck },
                { title: "Nye øjne", desc: "Du opdager de små fremskridt, dit barn gør.", icon: Eye },
                { title: "Mere rolig leder", desc: "Du lærer at guide dit barn uden skældud.", icon: Smile },
                { title: "Gode vaner", desc: "Du får redskaber, der virker i mange år frem.", icon: TrendingUp }
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <motion.div 
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1, duration: 0.5 }}
                    className="relative bg-white p-6 rounded-2xl border border-stone-200/40 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col h-full"
                  >
                    {/* Number badge */}
                    <div className="absolute -top-3 left-6 w-8 h-8 rounded-full bg-[#4e6354] text-white flex items-center justify-center font-semibold text-sm shadow-md border-2 border-white">
                      {i + 1}
                    </div>
                    
                    <div className="pt-2 flex-grow">
                      <h4 className="text-lg font-semibold text-stone-900 mb-2 flex items-center gap-2 mt-2">
                        <Icon className="w-5 h-5 text-[#4e6354] shrink-0" />
                        {item.title}
                      </h4>
                      <p className="text-stone-600 text-sm leading-relaxed font-light">
                        {item.desc}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

        </div>

        {/* Watermark Overlay - Winding Road */}
        <div className="absolute inset-0 z-0 pointer-events-none opacity-[0.08] mix-blend-multiply">
          <img 
            src="https://assets.auxodev.dk/assets/eff3456d-e9a8-4cc4-936d-27bf51e95b8f/95574613-b9ad-4e75-84fb-52de2d444fa1.png" 
            alt="Vejentilbarnet vandmærke" 
            className="w-full h-full object-cover object-center scale-110 transition-transform duration-1000"
          />
        </div>
      </section>

      {/* Section 3: Sådan kommer du i gang (3 enkle trin) */}
      <section className="pt-28 md:pt-36 pb-8 md:pb-12 bg-gradient-to-b from-white to-stone-50 relative overflow-hidden">
        {/* Wave Divider at top of section */}
        <div className="absolute top-0 left-0 w-full overflow-hidden leading-none z-20 rotate-180">
          <svg 
            viewBox="0 0 1200 120" 
            preserveAspectRatio="none" 
            className="relative block w-full h-12 md:h-24 fill-stone-50"
          >
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V120H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"></path>
          </svg>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* Left Column: Beautiful Image with Green Glow */}
            <div className="lg:col-span-5 relative">
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="relative"
              >
                {/* Green glow effect behind the image */}
                <div className="absolute -inset-10 bg-[#4e6354]/20 rounded-full blur-3xl opacity-70 pointer-events-none z-0" />
                
                {/* Decorative background element */}
                <div className="absolute -inset-4 bg-[#4e6354]/5 rounded-3xl rounded-tr-[120px] rounded-bl-[120px] transform -rotate-2 scale-105 pointer-events-none z-0" />
                
                {/* Image Container */}
                <div className="aspect-[4/5] rounded-3xl rounded-tr-[100px] rounded-bl-[100px] overflow-hidden shadow-lg border border-stone-200/40 relative z-10">
                  <img 
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/eff3456d-e9a8-4cc4-936d-27bf51e95b8f/shutterstock-2143172079-1.jpg" 
                    alt="Sådan kommer du i gang med Marte Meo" 
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700 ease-out"
                  />
                </div>
              </motion.div>
            </div>

            {/* Right Column: 3 Steps Timeline */}
            <div className="lg:col-span-7">
              <div className="text-left mb-10">
                <h2 className="text-3xl md:text-4xl font-light text-stone-900 uppercase tracking-tight mt-2">
                  Sådan kommer du i gang
                </h2>
                <p className="text-stone-500 font-light mt-2">3 enkle trin til at starte jeres forløb</p>
                <div className="w-12 h-1 bg-[#4e6354] my-6"></div>
              </div>

              <div className="relative border-l-2 border-stone-200/60 ml-4 pl-2 space-y-8 mb-10">
                {[
                  { 
                    step: "Trin 1", 
                    title: "Book", 
                    desc: "Start med en gratis, uforpligtende tlf-samtale (15 min).",
                    icon: PhoneCall
                  },
                  { 
                    step: "Trin 2", 
                    title: "Film", 
                    desc: "Du modtager en nem guide til at filme og uploade sikkert.",
                    icon: Video
                  },
                  { 
                    step: "Trin 3", 
                    title: "Mød", 
                    desc: "Vi ses online, ser klippene sammen og lægger jeres plan.",
                    icon: Laptop
                  }
                ].map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <motion.div 
                      key={i}
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.1, duration: 0.5 }}
                      className="relative pl-8 sm:pl-10"
                    >
                      {/* Timeline Dot */}
                      <div className="absolute -left-[41px] top-1.5 w-8 h-8 rounded-full bg-[#4e6354] text-white flex items-center justify-center font-semibold text-sm shadow-md border-4 border-white">
                        {i + 1}
                      </div>
                      
                      <div className="bg-white p-6 rounded-2xl border border-stone-200/80 shadow-sm hover:shadow-md hover:border-[#4e6354]/30 transition-all duration-300">
                        <h4 className="text-lg font-semibold text-stone-900 mb-2 flex items-center gap-2">
                          <Icon className="w-5 h-5 text-[#4e6354]" />
                          {item.title}
                        </h4>
                        <p className="text-stone-600 text-sm leading-relaxed font-light">
                          {item.desc}
                        </p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              {/* CTA Button */}
              <div className="text-left pl-4">
                <Link 
                  to="/kontakt"
                  className="inline-flex items-center gap-2 bg-[#4e6354] text-white hover:bg-[#3d4e42] px-8 py-4 rounded-full transition-all duration-300 font-semibold text-sm tracking-wider uppercase shadow-md hover:shadow-lg hover:-translate-y-0.5"
                >
                  Ring mig op
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Section 4: Sådan filmer I jeres videoer */}
      <section className="pt-8 md:pt-12 pb-0 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-light text-stone-900 uppercase tracking-tight mt-2">
              Sådan filmer I jeres videoer
            </h2>
            <div className="w-12 h-1 bg-[#4e6354] mx-auto my-6"></div>
            <p className="text-lg text-stone-600 font-light leading-relaxed">
              I skal filme to korte videoer med jeres iPhone. Det er vigtigt, at kameraet står stabilt, og at man kan se ansigterne på både dig og dit barn undervejs.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Middagen */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white p-8 md:p-10 rounded-3xl border border-stone-200/30 shadow-sm flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between mb-6">
                  <span className="text-xs font-bold uppercase tracking-widest text-[#4e6354] bg-[#4e6354]/10 px-3.5 py-1.5 rounded-full">
                    Situation 1
                  </span>
                  <div className="flex items-center gap-1.5 text-stone-500 text-sm font-light">
                    <Clock className="w-4 h-4 text-[#4e6354]" />
                    7-10 minutter
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-stone-900 mb-4">Middagen (Struktureret situation)</h3>
                <p className="text-stone-600 font-light mb-6 leading-relaxed">
                  Sæt videoen i gang, når I sætter jer til bordet for at spise. Dette giver indblik i en struktureret hverdagssituation.
                </p>
                
                <div className="bg-stone-50 p-5 rounded-2xl border border-stone-200/20">
                  <h4 className="text-sm font-semibold text-stone-800 uppercase tracking-wider mb-2 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-[#4e6354]" />
                    Kameratip
                  </h4>
                  <p className="text-stone-600 text-sm font-light leading-relaxed">
                    Stil jeres iPhone op ad en kande eller en kop på bordet, så den filmer jer skråt fra siden. Begge ansigter skal være synlige.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Leg på gulvet */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white p-8 md:p-10 rounded-3xl border border-stone-200/30 shadow-sm flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between mb-6">
                  <span className="text-xs font-bold uppercase tracking-widest text-[#4e6354] bg-[#4e6354]/10 px-3.5 py-1.5 rounded-full">
                    Situation 2
                  </span>
                  <div className="flex items-center gap-1.5 text-stone-500 text-sm font-light">
                    <Clock className="w-4 h-4 text-[#4e6354]" />
                    7-10 minutter
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-stone-900 mb-4">Leg på gulvet (Fri situation)</h3>
                <p className="text-stone-600 font-light mb-6 leading-relaxed">
                  Find en aktivitet på gulvet, f.eks. rolleleg, klodser eller en bog. Dette viser det frie og ustrukturerede samspil.
                </p>
                
                <div className="bg-stone-50 p-5 rounded-2xl border border-stone-200/20">
                  <h4 className="text-sm font-semibold text-stone-800 uppercase tracking-wider mb-2 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-[#4e6354]" />
                    Kameratip
                  </h4>
                  <p className="text-stone-600 text-sm font-light leading-relaxed">
                    Placer jeres iPhone på gulvet et par meter fra jer, eller sæt den i en holder. Sørg for, at kameraet fanger jeres ansigtsudtryk, når I kigger på hinanden.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Sådan sender I videoerne & Sikkerhed (Nu som et elegant fold-ud element med et billede til højre) */}
          <div className="mt-6 pt-6 border-t border-stone-200/40">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              {/* Left Column: The Accordion & Info */}
              <div className="lg:col-span-7">
                <h3 className="text-2xl font-light text-stone-900 uppercase tracking-tight mb-4">
                  Sådan sender I videoerne
                </h3>
                <p className="text-stone-600 font-light leading-relaxed mb-6 text-sm">
                  Når videoerne er klar, må du ikke sende dem som almindelig sms eller i Messenger, da det ødelægger kvaliteten. Klik på en af de sikre løsninger nedenfor for at se vejledningen:
                </p>

                <div className="space-y-3 mb-8">
                  {/* Accordion Item 1: WeTransfer */}
                  <div className="border border-stone-200/60 rounded-2xl overflow-hidden bg-white shadow-sm">
                    <button
                      onClick={() => toggleAccordion(1)}
                      className="w-full flex items-center justify-between p-5 text-left font-medium text-stone-800 hover:bg-stone-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Send className="w-5 h-5 text-[#4e6354]" />
                        <span>Løsning 1: WeTransfer (Anbefalet)</span>
                      </div>
                      {activeAccordion === 1 ? (
                        <ChevronUp className="w-5 h-5 text-[#4e6354]" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-stone-400" />
                      )}
                    </button>
                    <AnimatePresence initial={false}>
                      {activeAccordion === 1 && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <div className="p-5 pt-0 border-t border-stone-100 text-stone-600 text-sm font-light leading-relaxed bg-stone-50/50">
                            Gå ind på <a href="https://wetransfer.com" target="_blank" rel="noopener noreferrer" className="text-[#4e6354] underline font-medium">wetransfer.com</a> på jeres iPhone, upload videoerne, og send dem til min e-mail. Det er gratis, hurtigt og bevarer den fulde videokvalitet.
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Accordion Item 2: Google Drive / iCloud */}
                  <div className="border border-stone-200/60 rounded-2xl overflow-hidden bg-white shadow-sm">
                    <button
                      onClick={() => toggleAccordion(2)}
                      className="w-full flex items-center justify-between p-5 text-left font-medium text-stone-800 hover:bg-stone-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Laptop className="w-5 h-5 text-[#4e6354]" />
                        <span>Løsning 2: Google Drive / iCloud Link</span>
                      </div>
                      {activeAccordion === 2 ? (
                        <ChevronUp className="w-5 h-5 text-[#4e6354]" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-stone-400" />
                      )}
                    </button>
                    <AnimatePresence initial={false}>
                      {activeAccordion === 2 && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <div className="p-5 pt-0 border-t border-stone-100 text-stone-600 text-sm font-light leading-relaxed bg-stone-50/50">
                            Upload videoerne til din foretrukne cloud-tjeneste (f.eks. Google Drive, OneDrive, Dropbox eller iCloud) og del et direkte link med min e-mail.
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Accordion Item 3: Hjemmesidens kontaktformular */}
                  <div className="border border-stone-200/60 rounded-2xl overflow-hidden bg-white shadow-sm">
                    <button
                      onClick={() => toggleAccordion(3)}
                      className="w-full flex items-center justify-between p-5 text-left font-medium text-stone-800 hover:bg-stone-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <FileUp className="w-5 h-5 text-[#4e6354]" />
                        <span>Løsning 3: Hjemmesidens kontaktformular (Billeder & Dokumenter)</span>
                      </div>
                      {activeAccordion === 3 ? (
                        <ChevronUp className="w-5 h-5 text-[#4e6354]" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-stone-400" />
                      )}
                    </button>
                    <AnimatePresence initial={false}>
                      {activeAccordion === 3 && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <div className="p-5 pt-0 border-t border-stone-100 text-stone-600 text-sm font-light leading-relaxed bg-stone-50/50">
                            Du kan uploade dine billeder og dokumenter direkte via vores <a href="/kontakt" className="text-[#4e6354] underline font-medium">kontaktformular</a>, når du vælger "Marte Meo" som emne. Bemærk, at videoer på grund af filstørrelse skal sendes via WeTransfer eller fildelingslink.
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>

                {/* GDPR Security Box (Kompakt og dæmpet) */}
                <div className="bg-[#4e6354]/5 border border-[#4e6354]/10 rounded-2xl p-4 flex items-start gap-3">
                  <Lock className="w-5 h-5 text-[#4e6354] shrink-0 mt-0.5" />
                  <p className="text-stone-600 text-xs font-light leading-relaxed">
                    <strong>🔒 Sikkerhed og GDPR:</strong> Videoerne bliver kun set af mig og jer, gemmes på en sikker og adgangskodet enhed, og slettes permanent, så snart vores forløb er slut.
                  </p>
                </div>
              </div>

              {/* Right Column: Image */}
              <div className="lg:col-span-5 relative mt-8 lg:mt-0">
                {/* Green Glow */}
                <div className="absolute inset-0 bg-[#4e6354]/20 rounded-full blur-3xl transform scale-90 -z-10" />
                <div className="aspect-[4/5] rounded-3xl rounded-tr-[100px] rounded-bl-[100px] overflow-hidden shadow-lg border border-stone-200/40 relative z-10">
                  <img 
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/eff3456d-e9a8-4cc4-936d-27bf51e95b8f/5b19b1f6-e31a-4205-83d0-bb137335da93.png" 
                    alt="Sikker overførsel og GDPR" 
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700 ease-out"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 6: Priser & Online Forløb (Nu som sin egen sektion) */}
      <section className="py-16 md:py-24 bg-white border-t border-stone-200/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-light text-stone-900 uppercase tracking-tight mt-2">
                Priser & Online Forløb Marte meo
              </h2>
              <div className="w-12 h-1 bg-[#4e6354] mx-auto my-6"></div>
              <p className="text-lg text-stone-600 font-light leading-relaxed">
                Alle forløb foregår online via <strong>[Zoom/Teams]</strong>. Du filmer blot 5-10 minutter af din hverdag med mobilen og uploader det sikkert til mig. Derefter mødes vi online og finder jeres styrker.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto items-stretch">
            {/* Det korte forløb */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-[#4e6354] text-white p-8 md:p-10 rounded-3xl border border-[#4e6354]/20 shadow-lg hover:shadow-xl transition-all duration-300 flex flex-col justify-between relative"
            >
              <div>
                <div className="flex items-center gap-2 text-white mb-4">
                  <span className="w-2.5 h-2.5 rotate-45 bg-white/60 inline-block shrink-0" />
                  <span className="text-xs font-bold uppercase tracking-widest bg-white/20 px-3 py-1 rounded-full">
                    Fokuseret hjælp
                  </span>
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">Det korte forløb</h3>
                <p className="text-stone-200 text-sm font-medium mb-6">God til én specifik udfordring, fx putning eller måltider.</p>
                
                <div className="w-full h-px bg-white/20 my-6" />

                <ul className="space-y-4 mb-8">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-stone-200 shrink-0 mt-0.5" />
                    <span className="text-stone-100 text-sm font-light">
                      <strong>Indhold:</strong> 1 forsamtale + 2 videoanalyser + 2 online tilbagemeldinger.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-stone-200 shrink-0 mt-0.5" />
                    <span className="text-stone-100 text-sm font-light">
                      <strong>Varighed:</strong> 4-6 uger.
                    </span>
                  </li>
                </ul>
              </div>

              <div>
                <div className="w-full h-px bg-white/20 my-6" />
                <div className="flex items-baseline gap-1 mb-6">
                  <span className="text-3xl font-bold text-white">2.450,-</span>
                  <span className="text-stone-300 text-sm font-light">kr.</span>
                </div>
                <Link 
                  to="/kontakt"
                  className="w-full inline-flex items-center justify-center bg-white text-[#4e6354] hover:bg-stone-100 px-6 py-3.5 rounded-full transition-all duration-300 font-semibold text-xs tracking-wider uppercase shadow-sm hover:shadow-md"
                >
                  Vælg dette forløb
                </Link>
              </div>
            </motion.div>

            {/* Det lange forløb */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-[#4e6354] text-white p-8 md:p-10 rounded-3xl border border-[#4e6354]/20 shadow-lg hover:shadow-xl transition-all duration-300 flex flex-col justify-between relative"
            >
              <div>
                <div className="flex items-center gap-2 text-white mb-4">
                  <span className="w-2.5 h-2.5 rotate-45 bg-white/60 inline-block shrink-0" />
                  <span className="text-xs font-bold uppercase tracking-widest bg-white/20 px-3 py-1 rounded-full">
                    Gennemgribende forandring
                  </span>
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">Det lange forløb</h3>
                <p className="text-stone-200 text-sm font-medium mb-6">Til dig, der vil skabe varig trivsel og dyb indsigt i barnets udvikling.</p>
                
                <div className="w-full h-px bg-white/20 my-6" />

                <ul className="space-y-4 mb-8">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-stone-200 shrink-0 mt-0.5" />
                    <span className="text-stone-100 text-sm font-light">
                      <strong>Indhold:</strong> 1 forsamtale + 4 videoanalyser + 4 online tilbagemeldinger + mail-support.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-stone-200 shrink-0 mt-0.5" />
                    <span className="text-stone-100 text-sm font-light">
                      <strong>Varighed:</strong> 3-4 måneder.
                    </span>
                  </li>
                </ul>
              </div>

              <div>
                <div className="w-full h-px bg-white/20 my-6" />
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-3xl font-bold text-white">4.850,-</span>
                  <span className="text-stone-300 text-sm font-light">kr.</span>
                </div>
                <p className="text-xs text-stone-300 font-light mb-6">(Mulighed for ratebetaling)</p>
                <Link 
                  to="/kontakt"
                  className="w-full inline-flex items-center justify-center bg-white text-[#4e6354] hover:bg-stone-100 px-6 py-3.5 rounded-full transition-all duration-300 font-semibold text-xs tracking-wider uppercase shadow-sm hover:shadow-md"
                >
                  Vælg dette forløb
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
